import { supabase } from '../lib/supabase';
import { notificationDatabaseService } from './notificationDatabaseService';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * notificationService — الخدمة الموحدة للإشعارات
 *
 * تُرسل في آنٍ واحد:
 *   1. إشعار داخل التطبيق (جدول notifications) ← يظهر في جرس الإشعارات
 *   2. FCM Push Notification ← يصل للجوال حتى لو التطبيق مغلق
 */

/**
 * يرسل إشعاراً للمستخدم.
 * @param {{
 *   userId: string,
 *   type: 'booking'|'payment'|'delivery'|'system',
 *   title: string,
 *   message: string,
 *   bookingId?: string,
 *   fcmData?: object,   // بيانات إضافية للـ Push (مثل type, booking_id)
 *   skipInApp?: boolean // لا تضيف للـ DB (اختياري)
 * }} opts
 */
export async function sendNotification({ userId, type, title, message, bookingId, fcmData, skipInApp = false }) {
  const results = { inApp: false, fcm: false };

  // 1. إشعار داخلي في DB
  if (!skipInApp) {
    try {
      await notificationDatabaseService.createNotification({
        user_id: userId,
        type,
        title,
        message,
        related_booking_id: bookingId || null,
      });
      results.inApp = true;
    } catch (err) {
      console.warn('[notificationService] In-app notification failed:', err);
    }
  }

  // 2. FCM Push — awaited so WKWebView/navigation doesn't kill the request
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const bearer = session?.access_token || SUPABASE_ANON_KEY;
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${bearer}`,
      apikey: SUPABASE_ANON_KEY || '',
    };

    console.log(`[FCM] → sending to user_id=${userId} title="${title}"`);

    const res = await fetch(`${SUPABASE_URL}/functions/v1/send-fcm-notification`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        user_id: userId,
        title,
        body: message,
        data: { booking_id: bookingId, ...fcmData },
      }),
    });

    const json = await res.json().catch(() => ({}));
    console.log(`[FCM] ← status=${res.status}`, JSON.stringify(json));

    if (res.ok) {
      results.fcm = true;
    } else {
      console.warn('[notificationService] FCM failed:', JSON.stringify(json));
    }
  } catch (err) {
    console.warn('[notificationService] FCM error:', err);
  }

  return results;
}

// ─── Convenience helpers ──────────────────────────────────────

/** المصور قبل الحجز — يُخطَر العميل */
export function notifyCustomerPhotographerAccepted(customerId, bookingId) {
  return sendNotification({
    userId: customerId,
    type: 'booking',
    title: '🎉 وافق المصور على حجزك!',
    message: 'أكمل الدفع الآن لتأكيد الجلسة. لديك 60 دقيقة.',
    bookingId,
    fcmData: { type: 'AWAITING_PAYMENT' },
  });
}

/** العميل دفع — يُخطَر المصور */
export function notifyPhotographerPaymentConfirmed(photographerId, bookingId) {
  return sendNotification({
    userId: photographerId,
    type: 'payment',
    title: '💰 تم تأكيد الدفع',
    message: 'أكمل العميل الدفع. الجلسة مؤكدة!',
    bookingId,
    fcmData: { type: 'PAYMENT_CONFIRMED' },
  });
}

/** المصور رفع الملفات وسلّمها — يُخطَر العميل */
export function notifyCustomerFilesDelivered(customerId, bookingId) {
  return sendNotification({
    userId: customerId,
    type: 'delivery',
    title: '📸 صورك جاهزة للتحميل!',
    message: 'قام المصور برفع ملفاتك. اضغط لعرضها وتحميلها.',
    bookingId,
    fcmData: { type: 'FILES_DELIVERED' },
  });
}

/** الحجز انتهت صلاحيته — يُخطَر العميل */
export function notifyCustomerBookingExpired(customerId, bookingId) {
  return sendNotification({
    userId: customerId,
    type: 'booking',
    title: 'انتهت مهلة الحجز',
    message: 'لم يتم العثور على مصور متاح. يمكنك إعادة المحاولة.',
    bookingId,
    fcmData: { type: 'BOOKING_EXPIRED' },
  });
}

/** العميل ألغى — يُخطَر المصور */
export function notifyPhotographerCustomerCancelled(photographerId, bookingId) {
  return sendNotification({
    userId: photographerId,
    type: 'booking',
    title: 'تم إلغاء الحجز',
    message: 'ألغى العميل الحجز.',
    bookingId,
    fcmData: { type: 'CUSTOMER_CANCELLED' },
  });
}

/** حجز جديد — يُخطَر المصور (Push فقط؛ الإشعار الداخلي من تريجر التعيين) */
export function notifyPhotographerNewBooking(photographerId, bookingId) {
  return sendNotification({
    userId: photographerId,
    type: 'booking',
    title: '📸 لديك طلب حجز جديد!',
    message: 'لديك 3 دقائق للرد. افتح التطبيق الآن.',
    bookingId,
    skipInApp: true,
    fcmData: { type: 'NEW_BOOKING' },
  });
}

/** المصور رفض — يُخطَر العميل بالبحث عن مصور آخر */
export function notifyCustomerPhotographerDeclined(customerId, bookingId) {
  return sendNotification({
    userId: customerId,
    type: 'booking',
    title: 'جارٍ البحث عن مصور آخر',
    message: 'اعتذر المصور عن الحجز. نبحث لك عن مصور آخر الآن.',
    bookingId,
    fcmData: { type: 'PHOTOGRAPHER_DECLINED' },
  });
}

/** المشرف ألغى الحجز — يُخطَر العميل */
export function notifyCustomerAdminCancelled(customerId, bookingId) {
  return sendNotification({
    userId: customerId,
    type: 'booking',
    title: 'تم إلغاء حجزك',
    message: 'تم إلغاء حجزك من قِبل إدارة المنصة. للاستفسار تواصل معنا.',
    bookingId,
    fcmData: { type: 'ADMIN_CANCELLED' },
  });
}

/** المشرف ألغى الحجز — يُخطَر المصور */
export function notifyPhotographerAdminCancelled(photographerId, bookingId) {
  return sendNotification({
    userId: photographerId,
    type: 'booking',
    title: 'تم إلغاء الحجز',
    message: 'تم إلغاء هذا الحجز من قِبل إدارة المنصة.',
    bookingId,
    fcmData: { type: 'ADMIN_CANCELLED' },
  });
}

/** اكتملت الجلسة — يُخطَر العميل */
export function notifyCustomerSessionCompleted(customerId, bookingId) {
  return sendNotification({
    userId: customerId,
    type: 'booking',
    title: '✅ اكتملت جلستك!',
    message: 'تم تسجيل انتهاء الجلسة. سيرفع المصور الصور قريباً.',
    bookingId,
    fcmData: { type: 'SESSION_COMPLETED' },
  });
}

/** الحجز انتهت صلاحيته دون مصور — يُخطَر المصور (إن وُجد) */
export function notifyPhotographerBookingExpired(photographerId, bookingId) {
  return sendNotification({
    userId: photographerId,
    type: 'booking',
    title: 'انتهت صلاحية الحجز',
    message: 'انتهت مهلة الرد على هذا الحجز وتم إلغاؤه.',
    bookingId,
    fcmData: { type: 'BOOKING_EXPIRED' },
  });
}

// ─── Cancellation notifications ───────────────────────────────────────────────

/** انسحاب فوري من المصور — يُخطَر العميل مباشرةً */
export function notifyPhotographerWithdrew(customerId, bookingId) {
  return sendNotification({
    userId: customerId,
    type: 'booking',
    title: '⚠️ اعتذر المصور عن الحضور',
    message: '. نرجو فتح التطبيق واختيار مصور آخر او استرداد المبلغ.',
    bookingId,
    fcmData: { type: 'PHOTOGRAPHER_WITHDREW' },
  });
}

/** طلب إلغاء جديد من العميل — يُخطَر المصور */
export function notifyCancellationRequested(otherPartyId, requesterType, bookingId, feePct = 0) {
  const isCustomerRequest = requesterType === 'customer';
  const title = isCustomerRequest
    ? '⚠️ طلب إلغاء من العميل'
    : '⚠️ طلب انسحاب من المصور';
  const message = isCustomerRequest => isCustomerRequest
    ? `طلب العميل إلغاء الحجز برسوم ${feePct}%. بانتظار موافقة الإدارة.`
    : 'طلب المصور الانسحاب من الحجز. سيتم استرداد مبلغك كاملاً بعد موافقة الإدارة.';
  return sendNotification({
    userId: otherPartyId,
    type: 'booking',
    title,
    message: message(isCustomerRequest),
    bookingId,
    fcmData: { type: 'CANCELLATION_REQUESTED' },
  });
}

/** قرار المشرف على طلب الإلغاء — يُخطَر العميل، ويُخطَر المصور عند القبول فقط */
export function notifyCancellationDecision(customerId, photographerId, approved, feePct = 0, bookingId) {
  const refundPct = 100 - feePct;
  if (approved) {
    // إشعار العميل
    if (customerId) sendNotification({
      userId: customerId,
      type: 'booking',
      title: '✅ تم قبول طلب الإلغاء',
      message: feePct > 0
        ? `تمت الموافقة على إلغاء حجزك. سيُردّ إليك ${refundPct}% من المبلغ خلال 3-5 أيام عمل.`
        : 'تمت الموافقة على إلغاء حجزك. سيُردّ إليك المبلغ كاملاً خلال 3-5 أيام عمل.',
      bookingId,
      fcmData: { type: 'CANCELLATION_APPROVED' },
    });
    // إشعار المصور — الحجز أُلغي من العميل
    if (photographerId) sendNotification({
      userId: photographerId,
      type: 'booking',
      title: '🚫 ألغى العميل الحجز',
      message: 'قام العميل بإلغاء الحجز .',
      bookingId,
      fcmData: { type: 'CANCELLATION_APPROVED' },
    });
  } else {
    // رفض — إشعار العميل فقط
    if (customerId) sendNotification({
      userId: customerId,
      type: 'booking',
      title: '❌ رُفض طلب الإلغاء',
      message: 'رفضت الإدارة طلب إلغاء الحجز. الحجز لا يزال مؤكداً.',
      bookingId,
      fcmData: { type: 'CANCELLATION_REJECTED' },
    });
  }
}

// ─── Session reminders (called from edge function; also exported for manual use) ──

export function notifySessionReminder90min(userId, bookingId) {
  return sendNotification({
    userId,
    type: 'booking',
    title: 'تذكير حجز',
    message: 'حبينا نذكرك بجلستك، تبدا بعد ساعة ونصف.. خليك جاهز',
    bookingId,
    fcmData: { type: 'SESSION_REMINDER_90MIN' },
  });
}

export function notifySessionStart(userId, bookingId) {
  return sendNotification({
    userId,
    type: 'booking',
    title: 'موعد جلستك الآن',
    message: 'حان وقت الجلسة الآن نتمنى لك جلسة ممتعة',
    bookingId,
    fcmData: { type: 'SESSION_START' },
  });
}

// ─── Dispute notifications ──────────────────────────────────────────────────

/** نزاع تسليم — يُخطَر المصور برفع شكوى عليه */
export function notifyPhotographerDeliveryDispute(photographerId, bookingId, disputeTypeLabel) {
  return sendNotification({
    userId: photographerId,
    type: 'booking',
    title: '⚠️ شكوى على تسليم',
    message: `رُفعت شكوى بخصوص جلستك (${disputeTypeLabel || 'مشكلة في التسليم'}). تواصل مع فريق الدعم لإيضاح الأمر.`,
    bookingId,
    fcmData: { type: 'DELIVERY_DISPUTE', booking_id: bookingId },
  });
}

/** نزاع تسليم — يُخطَر المشرف بوجود نزاع جديد */
export async function notifyAdminDeliveryDispute({ customerId, customerName, bookingId, photographerName, disputeTypeLabel, ticketNumber }) {
  try {
    await supabase.from('admin_notifications').insert({
      type: 'dispute',
      title: '⚠️ نزاع تسليم جديد',
      message: `${customerName || 'عميل'} يشكو من: ${disputeTypeLabel || 'مشكلة في التسليم'} — حجز #${(bookingId || '').slice(0, 8)}${ticketNumber ? ` — تذكرة #${ticketNumber}` : ''}`,
      related_user_id: customerId || null,
      related_data: {
        booking_id: bookingId,
        photographer_name: photographerName,
        dispute_type: disputeTypeLabel,
        ticket_number: ticketNumber,
      },
    });
  } catch (err) {
    console.warn('[notificationService] Admin dispute notification failed:', err);
  }
}

/** تذكير التقييم — يُرسَل للعميل بعد تسليم الصور إذا لم يقيّم بعد */
export function notifyCustomerReviewReminder(customerId, bookingId, photographerName) {
  return sendNotification({
    userId: customerId,
    type: 'delivery',
    title: '⭐ قيّم تجربتك مع المصور',
    message: `كيف كانت تجربتك مع ${photographerName || 'المصور'}؟ تقييمك يساعد العملاء الآخرين`,
    bookingId,
    fcmData: { type: 'REVIEW_REMINDER', booking_id: bookingId },
  });
}
